<template>
  <div style="padding-top: 15px">
    <div class="alert alert-danger" v-if="!isValid()">{{$t('ProjectDetails.selectorLayersIncomplete')}}</div>
    <div class="alert alert-info" v-if="!current">{{$t('ProjectDetails.clickToMakeSelection')}}</div>
    <div class="alert alert-info" v-if="current">{{$t('ProjectDetails.secondClickToCompleteSelection')}}</div>
    <div class="table">
      <div :class="getRowClass(layer, 'upper')" v-for="(layer) in upLayers()">
        <div v-for="(x) in upTeeth"
             :class="getCellClass(layer, x)"
             v-on:click="handleSelection(layer, x)"
             :style="getSelectionStyle(layer, x)"
        >
          <div class="balloon-cnt"
               :data-balloon="getCellTooltip(layer, x)"
               :data-balloon-pos="indexOf(x) < 8 ? 'right' : 'left'"
               v-html="getSelectionText(layer, x)"
          ></div>
        </div>
        <div :class="`cell wide ${getLayerLabelClass(layer)}`">
          <span v-html="$t(layer.label)"></span>
          <i v-if="layer.required" class="color-orange fa fa-asterisk" aria-hidden="true"></i>
          <a v-if="layer.tooltip" :data-balloon-length="layer.tooltipLength" :data-balloon="$t(layer.tooltip)" :data-balloon-pos="layer.tooltipOrientation">
            <i class="fa fa-info-circle"></i>
          </a>
        </div>
      </div>
      <div class="row upper labels">
        <div v-for="(x) in upTeeth" :class="`cell tooth-${x} ${isToothSelected(x) ? 'bold' : ''}`">{{x}}</div>
        <div class="cell wide">{{$t('ProjectDetails.OK')}}</div>
      </div>
      <div :class="`row upper images`" v-if="teethView">
        <div v-for="(x) in upTeeth" :class="`cell tooth-${x}`" v-on:click="handleSelection(primaryUpLayer(), x)">
          <ToothSVG :tooth="x" :fill-color="toothFillColor(x)" :stroke-color="toothStrokeColor(x)" :view="teethView"/>
        </div>
        <div class="cell wide"></div>
      </div>
      <div :class="`row lower images`" v-if="teethView">
        <div v-for="(x) in loTeeth" :class="`cell tooth-${x}`" v-on:click="handleSelection(primaryLoLayer(), x)">
          <ToothSVG :tooth="x" :fill-color="toothFillColor(x)" :stroke-color="toothStrokeColor(x)" :view="teethView"/>
        </div>
        <div class="cell wide"></div>
      </div>
      <div class="row lower labels">
        <div v-for="(x) in loTeeth" :class="`cell tooth-${x} ${isToothSelected(x) ? 'bold' : ''}`">{{x}}</div>
        <div class="cell wide">{{$t('ProjectDetails.UK')}}</div>
      </div>
      <div :class="getRowClass(layer, 'lower')" v-for="(layer) in loLayers()">
        <div v-for="(x) in loTeeth"
             :class="getCellClass(layer, x)"
             v-on:click="handleSelection(layer, x)"
             :style="getSelectionStyle(layer, x)"
        >
          <div class="balloon-cnt"
               :data-balloon="getCellTooltip(layer, x)"
               :data-balloon-pos="indexOf(x) >= 24 ? 'right' : 'left'"
               v-html="getSelectionText(layer, x)"
          ></div>
        </div>
        <div :class="`cell wide ${getLayerLabelClass(layer)}`">
          <span v-html="$t(layer.label)"></span>
          <i v-if="layer.required" class="color-orange fa fa-asterisk" aria-hidden="true"></i>
          <a v-if="layer.tooltip" :data-balloon-length="layer.tooltipLength" :data-balloon="$t(layer.tooltip)" :data-balloon-pos="layer.tooltipOrientation">
            <i class="fa fa-info-circle"></i>
          </a>
        </div>
      </div>
    </div>
    <div v-if="isPrintout">
      <div v-for="(x, layer) in selections">
        <div v-for="(data, range) in x">
          <h3>{{$t(layers.find(({id: _}) => _ === layer).label)}}, {{$t('ProjectDetails.toothSelection')}}: {{range}}</h3>
          <div style="white-space: pre">{{getSelectionTooltip(layer, range, data)}}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import FormsMixin from '../Mixins/Forms.js'
  import ToothSVG from '../Elements/ToothSVG.vue'

  export default {
    mixins: [ FormsMixin ],
    components: {ToothSVG},
    props: {
      layers: { type: Array, default: [] },
      picker: Object,
      formValues: Object,
      readonly: Boolean,
      isPrintout: Boolean,
      teethView: String,
    },
    watch: {
      formValues(formValues) { // watch it
        this.selections = this.processFormValues(formValues, this.getLayers());
        this.validate();
      }
    },
    mounted() {
      this.validate();
    },
    data() {
      return {
        upTeeth: [
          ...Array(8).fill(0).map((i, x) => '1' + (8 - x)),
          ...Array(8).fill(0).map((i, x) => '2' + (1 + x)),
        ],
        loTeeth: [
          ...Array(8).fill(0).map((i, x) => '4' + (8 - x)),
          ...Array(8).fill(0).map((i, x) => '3' + (1 + x)),
        ],
        current: null,
        activeTab: null,
        selections: this.processFormValues(this.formValues, this.getLayers()),
        lastSelection: null,
        lastSelectionLayer: null,
      }
    },
    computed: {
      teeth() {
        return [].concat(this.upTeeth, [...this.loTeeth].reverse());
      }
    },
    methods: {
      upLayers() {
        return this.getLayers()
                .filter(({applyTo: _}) => _ === 'both' || _ === 'upper')
                .filter(x => this.meetsRequirements({requires: x.showIf}));
      },
      loLayers() {
        return this.getLayers()
                .filter(({applyTo: _}) => _ === 'both' || _ === 'lower')
                .reverse();
      },
      primaryUpLayer() {
        return this.upLayers().slice().pop();
      },
      primaryLoLayer() {
        return this.loLayers().slice().shift();
      },
      validate() {
        this.$forceUpdate();
        setTimeout(() => this.$emit('valid', this.getStatus()), 1);
      },
      isValid() {
        return this.upLayers().every(layer => this.isLayerValid(layer)) && this.loLayers().every(layer => this.isLayerValid(layer))
      },
      toothFillColor(x) {
        return this.isToothSelected(x) ? '#fff' : '#fff'
      },
      toothStrokeColor(x) {
        return this.isToothSelected(x) ? '#000' : '#999'
      },
      isToothSelected(tooth) {
        const selections = Object.keys(Object.assign({}, ...Object.values(this.selections)));
        if (selections.length === 0) {
          return true;
        }

        return selections.map(this.range).some(x => x.indexOf(tooth) >= 0);
      },
      handleToothSelectedInput(value, field) {
        this.$emit('change', value, field);
      },
      getRowClass({id, sameJaw}, position) {
        let selectable = this.isMaxUnitsValid(id);
        if (sameJaw && Object.keys(this.selections[id] || {}).length) {
          const [start] = Object.keys(this.selections[id] || {}).map(this.range).pop() || [];
          const currentJaw = this.indexOf(start) < 16 ? 'upper' : 'lower';

          if (position !== currentJaw) {
            selectable = false ;
          }
        }

        if (this.readonly) {
          selectable = false;
        }

        return `row ${selectable ? 'selectable' : ''} ${position}`;
      },
      getCellClass({id}, tooth) {
        const selections = Object.keys(this.selections[id] || {});
        const selected = selections.map(this.range).some(x => x.indexOf(tooth) >= 0);
        if (selected) {
          const start = selections.map(this.range).some(x => x.indexOf(tooth) === 0);
          const end = selections.map(this.range).some(x => x.indexOf(tooth) === x.length - 1);
          return `cell selection ${start ? 'start' : ''} ${end ? 'end' : ''} tooth-${tooth}`;
        }

        if (this.current && this.current.layer === id && this.current.started === tooth) {
          const start = this.current.possible.indexOf(tooth) === 0;
          const end = this.current.possible.indexOf(tooth) === this.current.possible.length - 1;

          return `cell started ${start ? 'start' : ''} ${end ? 'end' : ''} tooth-${tooth}`;
        }

        const possible = this.current && this.current.layer === id && this.current.possible.indexOf(tooth) >= 0;
        if (possible) {
          const start = this.current.possible.indexOf(tooth) === 0;
          const end = this.current.possible.indexOf(tooth) === this.current.possible.length - 1;

          return `cell possible ${start ? 'start' : ''} ${end ? 'end' : ''} tooth-${tooth}`;
        }

        return `cell tooth-${tooth}`;
      },
      getCellTooltip({id, popup, label}, tooth) {
        const selections = Object.keys(this.selections[id] || {});
        const selected = selections.find(x => this.range(x).indexOf(tooth) >= 0);
        if (selected) {
          return `${this.$t('ProjectDetails.toothSelection')}: ${selected}\n` + this.getSelectionTooltip(id, selected, this.selections[id][selected])
        }
      },
      getSelectionTooltip(id, range, {values}) {
        const {popup = []} = this.getLayer(id);
        return Object.entries(values)
          .filter(([, v]) => v != null)
          .map(([key, value]) => this.findKeyValueFor(popup, key, value).join(': '))
          .join('\n');
      },
      findKeyValueFor(popup, key, value) {
        for(const tab of popup || []) {
          for(const field of tab.form || []) {
            if (field.id === key) {
              const label = this.$t(field.label);
              if (field.options) {
                value = this.$t((field.options.find(({value: _}) => value === _) || {}).label) || value;
              } else if (field.productTree) {
                value = value.selection ? value.selection.join(' > ') : value;
              }
              return [label, value];
            }
          }
        }

        return [key, value];
      },
      indexOf(tooth) {
        if (tooth === 'min') return Number.MIN_SAFE_INTEGER;
        if (tooth === 'max') return Number.MAX_SAFE_INTEGER;
        return this.teeth.indexOf(tooth);
      },
      distance(from, to) {
        return this.indexOf(to) - this.indexOf(from);
      },
      range(range) {
        const [from, to = from] = range.split('-');
        return this.teeth
          .filter(x => this.distance(from, x) >= 0)
          .filter(x => this.distance(x, to) >= 0);
      },
      handlePopupResult(mode, layer, range, values, itemnums, finder, sections) {
        console.log('handlePopupResult', mode, layer, range, values, itemnums);
        switch (mode) {
          case 'save':
            this.selections[layer][range] = Object.assign({}, this.selections[layer][range], {
              values,
              itemnums,
              finder,
              sections,
            });
            this.lastSelection = this.selections[layer][range];
            this.lastSelectionLayer = layer;
            this.$emit('change', layer, {
              [range]: {
                values,
                itemnums,
                finder,
                sections,
              }
            });
            break;
          case 'remove':
            delete this.selections[layer][range];
            this.$emit('change', layer, {[range]: undefined});
        }
        this.validate();
      },
      isUpperTooth(tooth) {
        return this.distance(tooth, '28') >= 0;
      },
      isLowerTooth(tooth) {
        return this.distance(tooth, '38') <= 0;
      },
      getRanges(layer) {
        return Object.keys(this.selections[layer] || {});
      },
      getUpperRanges(layer) {
        return this.getRanges(layer).filter(range => this.isUpperTooth(this.range(range).pop()));
      },
      getLowerRanges(layer) {
        return this.getRanges(layer).filter(range => this.isLowerTooth(this.range(range).shift()));
      },
      isMaxUnitsValid(layer) {
        const ranges = this.getRanges(layer);
        const upperRanges = this.getUpperRanges(layer);
        const lowerRanges = this.getLowerRanges(layer);

        const {maxunits, maxupper, maxlower} = this.getLayer(layer);
        return (ranges.length <= (maxunits || 32))
            && (upperRanges.length <= (maxupper || 16))
            && (lowerRanges.length <= (maxlower || 16));
      },
      isMinUnitsValid(layer) {
        const ranges = this.getRanges(layer);

        const upperRanges = this.getUpperRanges(layer);
        const lowerRanges = this.getLowerRanges(layer);

        const {minunits, minupper, minlower} = this.getLayer(layer);
        return (ranges.length >= (minunits || 0))
            && (upperRanges.length >= (minupper || 0))
            && (lowerRanges.length >= (minlower || 0));

      },
      handleSelection({id, range, maxunits, maxupper, maxlower, applyTo, popup, sameJaw, withinJaw}, tooth) {
        if (this.readonly) {
          return;
        }

        if (this.current && (this.current.layer !== id || this.current.possible.indexOf(tooth) < 0)) {
          console.log('cancel');
          this.current = null;
          return;
        }

        if (!this.current && Object.keys(this.selections[id] || {})
          .some(range => {
            if (this.range(range).indexOf(tooth) >= 0) {
              console.log('here');
              if (!Array.isArray(popup)) {
                this.handlePopupResult('remove', id, range);
                return true;
              }

              const data = JSON.parse(JSON.stringify(this.selections[id][range]));
              this.$emit('popup', [popup || [], data, {
                readonly: this.readonly,
                editing: true,
                title: 'Configurator.selectIndication',
                bodyClass: 'nopadding',
              }, this.handlePopupResult]);
              return true;
            }
          })
        ) {
          console.log('cancel 2');
          this.current = null;
          return ;
        }

        if (this.readonly) {
          console.log('readonly');
          return;
        }

        if (this.getRanges(id).length >= (maxunits || 32)) {
          console.log('maxunits reached', maxunits);
          return ;
        }

        if (this.isUpperTooth(tooth) && this.getUpperRanges(id).length >= (maxupper || 16)) {
          console.log('maxupper reached', maxupper);
          return
        }

        if (this.isLowerTooth(tooth) && this.getLowerRanges(id).length >= (maxlower || 16)) {
          console.log('maxlower reached', maxlower);
          return
        }

        if (sameJaw && Object.keys(this.selections[id] || {}).length) {
          const [start] = Object.keys(this.selections[id] || {}).map(this.range).pop() || [];
          const currentJaw = this.indexOf(start) < 16 ? 'upper' : 'lower';

          if ((this.indexOf(tooth) < 16 ? 'upper' : 'lower') !== currentJaw) {
            console.log('sameJaw restriction');
            return ;
          }
        }

        if (!this.current && range > 1) {
          if (sameJaw || withinJaw) {
            applyTo = this.indexOf(tooth) < 16 ? 'upper' : 'lower';
          }

          const selections = Object.keys(this.selections[id] || {}).map(this.range);

          const leftBound = selections
            .map(x => [...x].pop())
            .filter(x => this.distance(x, tooth) > 0)
            .reduce((p, c) => this.indexOf(p) > this.indexOf(c) ? p : c, applyTo === 'lower' ? '28' : 'min');
          const rightBound = selections
            .map(x => [...x].shift())
            .filter(x => this.distance(tooth, x) > 0)
            .reduce((p, c) => this.indexOf(p) < this.indexOf(c) ? p : c, applyTo === 'upper' ? '38' : 'max');

          this.current = {
            layer: id,
            started: tooth,
            possible: [
              ...this.teeth.filter(x =>
                Math.abs(this.distance(tooth, x)) < range
                  && this.distance(leftBound, x) > 0
                  && this.distance(x, rightBound) > 0)
            ],
          };

          return;
        }

        if (!this.current && range === 1) {
          this.current = {
            layer: id,
            started: tooth,
            possible: [tooth],
          }
          console.log('set current', this.current);
        }

        if (this.current) {
          console.log('handle current', this.current);

          const started = this.current.started;
          const end = tooth;

          const distance = this.distance(started, end);
          if (distance > 0) {
            tooth = `${started}-${end}`;
          }
          if (distance < 0) {
            tooth = `${end}-${started}`;
          }
          this.current = null;

          this.selections[id] = Object.assign({}, this.selections[id], {
            [tooth]: Object.assign({}, (this.selections[id] || {})[tooth], {
              layer: id,
              range: tooth,
              selection: {},
              values: {},
              itemnums: {},
              finder: [],
            }),
          });

          if (!Array.isArray(popup)) {
            this.handlePopupResult('save', id, tooth, {}, [], []);
          } else {
            this.$emit('popup', [popup || [], this.selections[id][tooth], {
              title: 'Configurator.selectIndication',
              bodyClass: 'nopadding',
              lastSelection: this.lastSelectionLayer === id ? this.lastSelection : null,
            }, this.handlePopupResult]);
          }
        }
      },
      getLayers() {
        return this.layers.filter(({showIf}) => this.meetsRequirements({requires: showIf}));
      },
      getLayer(id) {
        return this.getLayers().find(({id: _}) => _ === id);
      },
      getItemNums() {
        const sections = [];
        Object.keys(this.selections)
          .forEach(layer => {
            const ranges = Object.keys(this.selections[layer]);

            if (ranges.length < 1) {
              return
            }

            const upperRanges = ranges.filter(range => this.distance(this.range(range).pop(), '28') >= 0);
            const lowerRanges = ranges.filter(range => this.distance(this.range(range).shift(), '38') <= 0);

            const layerInfo = this.getLayer(layer);
            if (!layerInfo) {
              // THIS IS a side-effect, TODO: refactor for better impl
              delete this.selections[layer];
              return;
            }
            const {
              label,
              defaultItems,
              defaultUpperItems,
              defaultLowerItems,
              defaultToothItems,
              defaultUpperToothItems,
              defaultLowerToothItems,
            } = layerInfo;

            if (typeof defaultItems === 'object') {
              const items = {};
              Object.keys(defaultItems)
                .forEach(itemnum => {
                  items[itemnum] = (items[itemnum] || 0) + defaultItems[itemnum] * ranges.length;
                })

              if (Object.keys(items).length) {
                sections.push({
                  section: {
                    id: `${layer}`,
                    title: this.$t(label),
                  },
                  items
                })
              }
            }

            if (typeof defaultUpperItems === 'object') {
              const items = {};
              Object.keys(defaultUpperItems)
                .forEach(itemnum => {
                  items[itemnum] = (items[itemnum] || 0) + defaultUpperItems[itemnum] * upperRanges.length;
                })

              if (Object.keys(items).length) {
                sections.push({
                  section: {
                    id: `${layer}`,
                    title: this.$t(label),
                  },
                  items
                })
              }
            }

            if (typeof defaultLowerItems === 'object') {
              const items = {};
              Object.keys(defaultLowerItems)
                .forEach(itemnum => {
                  items[itemnum] = (items[itemnum] || 0) + defaultLowerItems[itemnum] * lowerRanges.length;
                })

              if (Object.keys(items).length) {
                sections.push({
                  section: {
                    id: `${layer}`,
                    title: this.$t(label),
                  },
                  items
                })
              }
            }


            
            if (typeof defaultToothItems === 'object') {
              const items = {};
              const amount = ranges.map(range => this.range(range).length).reduce((p, c) => p + c, 0);
              Object.keys(defaultToothItems)
                .forEach(itemnum => {
                  items[itemnum] = (items[itemnum] || 0) + defaultToothItems[itemnum] * amount;
                })

              if (Object.keys(items).length) {
                sections.push({
                  section: {
                    id: `${layer}`,
                    title: this.$t(label),
                  },
                  items
                })
              }
            }

            if (typeof defaultUpperToothItems === 'object') {
              const items = {};
              const amount = ranges.map(range => this.range(upperRanges).length).reduce((p, c) => p + c, 0);
              Object.keys(defaultUpperItems)
                .forEach(itemnum => {
                  items[itemnum] = (items[itemnum] || 0) + defaultUpperItems[itemnum] * amount;
                })

              if (Object.keys(items).length) {
                sections.push({
                  section: {
                    id: `${layer}`,
                    title: this.$t(label),
                  },
                  items
                })
              }
            }

            if (typeof defaultLowerToothItems === 'object') {
              const items = {};
              const amount = ranges.map(range => this.range(lowerRanges).length).reduce((p, c) => p + c, 0);
              Object.keys(defaultLowerToothItems)
                .forEach(itemnum => {
                  items[itemnum] = (items[itemnum] || 0) + defaultLowerToothItems[itemnum] * amount;
                })

              if (Object.keys(items).length) {
                sections.push({
                  section: {
                    id: `${layer}`,
                    title: this.$t(label),
                  },
                  items
                })
              }
            }


            ranges
              .forEach(range => {
                const distance = this.range(range).length;
                const items = {};
                (this.selections[layer][range]
                  .sections || [])
                  .forEach(({items: _}) => {
                    Object.keys(_).forEach(itemnum => {
                      items[itemnum] = (items[itemnum] || 0) + _[itemnum] * distance;
                    })
                  })

                if (Object.keys(items).length) {
                  sections.push({
                    section: {
                      id: `${layer}-${range}`,
                      title: this.$t(label) + ' ' + range,
                    },
                    items
                  })
                }
              })
          })

        return sections;
      },

      processFormValues(formValues, layers) {
        const data = Object
          .keys(formValues || {})
          .filter(key => layers.some(({id}) => id === key))
          .reduce((p, layer) => {
            p[layer] = {};

            const value = typeof formValues[layer] === 'string' ? JSON.parse(formValues[layer]) : {};

            Object.keys(value).forEach(range => {
              p[layer][range] = Object.assign({
                layer,
                range,
                selection: {},
              }, value[range]);
            });

            return p;
          }, {});

        return data;
      },

      getSelectionStyle(layer, tooth) {
        return `--selection-color: ${this.getSelectionColor(layer, tooth)}; color: ${this.getSelectionTextColor(layer, tooth)}`
      },

      getSelectionColor({id, color}, tooth) {
        if (!color) return '';
        const selections = Object.keys(this.selections[id] || {});
        const selected = selections.find(x => this.range(x).indexOf(tooth) >= 0);
        if (!selected) return '';

        if (typeof color === 'string') {
          return color;
        }

        const {
          field,
          options
        } = color;

        const {
          values
        } = this.selections[id][selected];

        return values[field] && options[values[field]] || '';
      },

      getSelectionTextColor({id, textColor}, tooth) {
        if (!textColor) return '';
        const selections = Object.keys(this.selections[id] || {});
        const selected = selections.find(x => this.range(x).indexOf(tooth) >= 0);
        if (!selected) return '';


        if (typeof textColor === 'string') {
          return textColor;
        }

        const {
          field,
          options
        } = textColor;

        const {
          values
        } = this.selections[id][selected];

        return values[field] && options[values[field]] || '';
      },

      getSelectionText({id, text}, tooth) {
        const selections = Object.keys(this.selections[id] || {});
        const selected = selections.find(x => this.range(x).indexOf(tooth) >= 0);
        if (!selected) return '';
        if (!text) return '✔';

        if (typeof text === 'string' || !text.field) {
          return this.$t(text) || '✔';
        }

        const {
          field,
          suffix = '',
          options = {},
        } = text;

        const {
          values
        } = this.selections[id][selected];

        return `${this.$t(options[values[field]] || values[field] || '✔')}${suffix}`;
      },

      isLayerValid({id, required}) {
        return (required !== true || this.getRanges(id).length > 0)
            && this.isMinUnitsValid(id)
            && this.isMaxUnitsValid(id)
        ;
      },

      getLayerLabelClass(layer) {
        const selections = Object.keys(this.selections[layer.id] || {});
        if (selections.length === null) {
          return ''
        }

        return this.isLayerValid(layer) ? 'valid' : 'invalid'
      },

      getStatus() {
        return this.getLayers().every((layer) => this.isLayerValid(layer));
      },
    }
  }
</script>

<style scoped>
  .table {
    width: 100%;
    display: table;
    border-collapse: collapse;

    --selection-color: green;
  }
  .row {
    display: table-row;
  }
  .cell {
    display: table-cell;
    text-align: center;
    border: 1px solid grey;
    position: relative;
  }
  .wide {
    width: 10vw;
    text-align: left;
  }
  .cell.wide.invalid {
    color: red;
  }

  .cell::after {
    content: ' ';
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: -1px;
    left: -1px;
    background-color: transparent;
    z-index: 0;
    opacity: 1;
    cursor: inherit;
    transition: all .1s ease-out;
  }

  .cell.selection {
    color: white;
  }

  .row.selectable .cell:not(.possible):not(.selection):not(.started):not(.wide):hover::after {
    cursor: pointer;
    transition: all .3s ease-in-out;
    opacity: 0.5;
    background-color: green;
    background-color: var(--selection-color, green);
    right: 10%;
    border-radius: 4px;
    left: 10%;
  }

  .selection::after {
    content: ' ';
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: -1px;
    left: -1px;
    background-color: green;
    background-color: var(--selection-color, green);
    z-index: 2;
    opacity: 0.75;
    cursor: pointer;
    transition: all .3s ease-in-out;
  }
  .started::after {
    content: ' ';
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: -1px;
    left: -1px;
    background-color: yellowgreen;
    z-index: 1;
    opacity: 0.75;
    cursor: pointer;
  }
  .possible::after {
    content: ' ';
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: -1px;
    left: -1px;
    background-color: yellow;
    z-index: 0;
    opacity: 0.75;
    cursor: pointer;
    transition: all .3s ease-in-out;
  }
  .upper .start::after, .lower .end::after {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    left: 10%;
  }
  .upper .end::after, .lower .start::after {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    right: 10%;
  }
  .row.labels .cell, .row .cell.wide {
    cursor: initial !important;
  }

  .row.labels .cell {
    font-weight: normal;
    transition: all .3s ease-in-out;
  }

  .row.labels .cell.wide, .row.labels .cell.bold {
    font-weight: bold;
  }

  .row.labels.lower .cell {
    border-top-width: 2px;
  }

  .row.labels.upper .cell {
    border-bottom-width: 2px;
  }

  .row.images .cell:not(.wide) {
    font-weight: normal;
    border-right-color: transparent;
  }

  .row.images.upper .cell {
    font-weight: normal;
    border-bottom-color: transparent;
  }

  .row.images.upper .cell {
    font-weight: normal;
    border-top-color: transparent;
  }

  .cell.tooth-11, .cell.tooth-41 {
    border-right-width: 2px;
  }

  .cell.tooth-21, .cell.tooth-31, .cell.wide {
    border-left-width: 2px;
  }

  .cell.wide {
    padding: 0 4px;
  }

  .balloon-cnt {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3;
    font-size: 1rem;
    line-height: 2rem;
  }

  [data-balloon]::after {
    white-space: pre
  }

  .images {
    cursor: pointer;
  }
</style>
